import { GetStaticProps, NextPage } from 'next';
import { SSRConfig } from 'next-i18next';
import { useTranslation } from 'next-i18next';
import i18nextConfig from 'next-i18next.config';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useState } from 'react';
import { CSSProp, css } from 'styled-components';
import tw, { styled } from 'twin.macro';
import { TRANSLATE } from 'core/util/translate';
import CModal from 'core/view/ConsultingsModal';
import { memberData } from 'core/view/membership/member.data';
import { typeData } from 'core/view/membership/type.data';

const Index: NextPage = () => {
  const [isCModalOpen, setIsCModalOpen] = useState(false);
  const { t, i18n } = useTranslation('membership');
  const lang = i18n.resolvedLanguage;

  const introductionDriveLink = (lang?: string) => {
    const l = lang as keyof typeof TRANSLATE;
    if (!TRANSLATE[l]) return TRANSLATE.ko.introductionDriveURL;
    return TRANSLATE[l].introductionDriveURL;
  };

  return (
    <>
      <DIV_OuterBox css={[tw`bg-[#fff]`]}>
        <div>
          <header css={[tw`pb-100 x_430:(pb-60)`]}>
            {t('header.subInfo') && (
              <h3
                css={[
                  tw`pb-12`,
                  tw`text-[#333] font-extrabold font-NanumSquare`,
                  tw`text-18 leading-18 x_430:(text-16 leading-16)`,
                ]}
              >
                {t('header.subInfo')}
              </h3>
            )}

            <h2
              css={[
                tw`text-[#111] font-bold font-OpenSans`,
                tw`text-38 leading-38 x_430:(text-30 leading-30)`,
              ]}
            >
              MEMBERSHIP
            </h2>
          </header>

          <SECTION_section>
            <DIV_Tag css={[tw`bg-[#f1f1f1] text-[#333]`]}>Introduction</DIV_Tag>
            <a
              css={[
                tw`flex m-auto max-w-max`,
                tw`py-16 px-18`,
                tw`rounded-10 border-1 border-solid border-[#5b809b]`,
              ]}
              target="_blank"
              rel="noreferrer"
              href={introductionDriveLink(lang)}
            >
              <img
                width={28}
                height={28}
                src={`${process.env.NEXT_PUBLIC_CLOUD_FRONT}/partners/ssak/icons/download.png`}
                alt="다운로드 아이콘"
              />
              <span
                css={[
                  tw`ml-16`,
                  tw`text-[#333]font-normal`,
                  tw`text-16 leading-32 x_1280:(text-14 leading-28)`,
                ]}
              >
                {t('downloadName')}
              </span>
            </a>
          </SECTION_section>

          {memberData.map((data, dataIndex) => (
            <SECTION_section
              key={`data-${dataIndex}`}
              css={[dataIndex === memberData.length - 1 ? tw`pb-0!` : ``]}
            >
              <DIV_Tag css={[tw`bg-[#f1f1f1] text-[#333]`]}>
                {data.label}
              </DIV_Tag>
              <section
                css={[
                  tw`grid`,
                  tw`grid-cols-[1fr 1fr 1fr]`,
                  tw`gap-[50px]`,
                  tw`x_1280:(gap-[25px])`,
                  tw`x_768:(grid-cols-[1fr 1fr] gap-[16px])`,
                  tw`[>a]:m-auto`,
                  css`
                    grid-auto-rows: -webkit-max-content;
                  `,
                ]}
              >
                {data.members.map((member, memberIndex) => {
                  const { imageProps, ...aprops } = member;
                  return (
                    <A_Member key={`member-${memberIndex}`} {...aprops}>
                      <img
                        width={308}
                        height={200}
                        {...imageProps}
                        css={[tw`object-fill`, tw`w-full`]}
                        alt={imageProps.alt}
                      />
                    </A_Member>
                  );
                })}
              </section>
            </SECTION_section>
          ))}
        </div>
      </DIV_OuterBox>

      <DIV_OuterBox css={[tw`bg-[#eee]`]}>
        <div>
          <SECTION_section>
            <DIV_Tag css={[tw`text-[#fff]bg-[#111]`]}>Member Type</DIV_Tag>

            <section
              css={tw`grid grid-cols-[1fr 1fr] md:grid-cols-[1fr 1fr 1fr] gap-[10px] x_1024:gap-[40px]`}
            >
              {typeData(t).map((type, index) => (
                <ARTICLE_TypeBox key={`type-${index}`} css={type.css}>
                  <img
                    src={type.imageProps.src}
                    srcSet={type.imageProps.srcSet}
                    alt={type.imageProps.alt}
                    width={105}
                    height={104}
                    loading="lazy"
                  />
                  <header>{type.title}</header>
                  {type.description}
                </ARTICLE_TypeBox>
              ))}
            </section>
          </SECTION_section>

          <SECTION_section css={tw`pb-0`}>
            <DIV_Tag css={[tw`text-[#fff] bg-[#111]`]}>Process</DIV_Tag>
            <div css={[tw`h-[40px]`]} />
            <section css={tw`w-full`}>
              <ul
                css={[
                  tw`grid gap-y-[0] grid-cols-[1fr 1fr 1fr 1fr] `,
                  tw`font-bold font-NanumSquare text-[#333] text-14 leading-25`,
                  tw`x_768:(gap-y-[50px] grid-cols-[156px 156px] text-12 leading-21 w-[fit-content] mx-auto)`,
                  tw`[& article]:(mx-auto mb-10)`,
                ]}
              >
                <li css={[tw`relative whitespace-pre-line`]}>
                  <Num num={1} />
                  {t('process.submit')}
                  <div
                    css={[
                      tw`absolute h-[2px] bg-[rgba(51, 51, 51, 0.1)]`,
                      tw` top-[18px] left-[calc(50% + 18px)] w-[calc(100% - 36px)]`,
                    ]}
                  />
                </li>
                <li css={[tw`relative`]}>
                  <Num num={2} />
                  {t('process.confirm')}
                  <div
                    css={[
                      tw`hidden md:block`,
                      tw`absolute h-[2px] bg-[rgba(51, 51, 51, 0.1)]`,
                      tw`top-[18px] left-[calc(50% + 18px)] w-[calc(100% - 36px)]`,
                    ]}
                  />
                  <div
                    css={[
                      tw`hidden x_768:block`,
                      tw`absolute h-[2px] bg-[rgba(51, 51, 51, 0.1)]`,
                      tw`right-[54px] rotate-[143deg] top-[75px] w-[187px]`,
                    ]}
                  />
                </li>
                <li css={[tw`relative`]}>
                  <Num num={3} />
                  {t('process.approved')}
                  <div
                    css={[
                      tw`absolute top-[18px] left-[calc(50% + 18px)] w-[calc(100% - 36px)] h-[2px]`,
                      tw`bg-[rgba(51, 51, 51, 0.1)]`,
                    ]}
                  />
                </li>
                <li>
                  <Num num={4} />
                  {t('process.joined')}
                </li>
              </ul>
            </section>
            <div css={[tw`h-[60px] md:h-[100px]`]} />
            <section
              css={[
                tw`w-[100%]`,
                tw`box-shadow[0px 4px 12px rgba(0, 0, 0, 0.15)]`,
                tw`pt-[40px] pb-[50px]`,
                tw`rounded-[10px]`,
              ]}
              style={{
                background:
                  'linear-gradient(180deg, #555555 0%, #333333 100%), linear-gradient(0deg, #DDDDDD, #DDDDDD), linear-gradient(0deg, #AE9249, #AE9249), #F5D04C',
              }}
            >
              <header
                css={[
                  tw`font-NotoSansCJKkr`,
                  tw`text-white`,
                  tw`text-[24px] md:text-[26px] font-weight[700] line-height[1.8em]`,
                  tw`whitespace-pre-line mb-30`,
                ]}
              >
                {t('contact.title')}
              </header>

              <div css={[tw`gap-[12px] flex items-center justify-center`]}>
                <button
                  onClick={() => setIsCModalOpen(true)}
                  css={[
                    tw`text-white`,
                    tw`font-OpenSans`,
                    tw`py-8 px-24 border[1px solid #fff] rounded-full`,
                  ]}
                >
                  {t('contact.linkName')}
                </button>
              </div>
            </section>

            <section
              css={[
                tw`flex items-center justify-center gap-[10px] flex-col md:flex-row`,
                tw`mt-[20px]`,
              ]}
            >
              <p
                css={[
                  tw`text-[14px] md:text-[14px] font-weight[500] line-height[2em] text-[#111]`,
                ]}
              >
                {t('office')}
              </p>
              <a
                href="mailto:hello@selfstoragekorea.org"
                css={[
                  tw`text-[14px] md:text-[14px] font-weight[400] line-height[2em] text-[#333]`,
                ]}
              >
                hello@selfstoragekorea.org
              </a>
            </section>
          </SECTION_section>
        </div>
      </DIV_OuterBox>
      <CModal isOpen={isCModalOpen} close={() => setIsCModalOpen(false)} />
    </>
  );
};

export default Index;

export const getStaticProps: GetStaticProps<SSRConfig> = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(
        locale as string,
        ['home', 'membership'],
        i18nextConfig,
      )),
    },
  };
};

const Num = ({ num }: { num: number }) => {
  return (
    <article
      css={[
        tw`w-[38px] aspect-ratio[1/1]`,
        tw`bg-[#333333]`,
        tw`flex items-center justify-center rounded-full`,
      ]}
    >
      <span
        css={[
          tw`font-OpenSans font-weight[700] text-[24px] line-height[1em] text-white`,
        ]}
      >
        {num}
      </span>
    </article>
  );
};

const DIV_OuterBox = styled.div<{ css?: CSSProp }>`
  > div {
    ${[
      tw`max-w-[1024px] m-auto box-content`,
      tw`text-center`,
      tw`px-80 pt-100 pb-160`,
      tw`x_768:(px-24 pt-80 pb-120)`,
    ]}
  }
  ${({ css }) => css};
`;

const SECTION_section = styled.div<{ css?: CSSProp }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${[tw`pb-100 x_430:(pb-60)`]};
  ${({ css }) => css};
`;

const DIV_Tag = styled.div<{ css?: CSSProp }>`
  ${[
    tw`block m-auto rounded-full max-w-max`,
    tw`px-12 py-2`,
    tw`mb-40 x_430:(mb-20)`,
    tw`font-bold font-OpenSans`,
    tw`text-14 leading-28`,
    tw`x_430:(text-12 leading-24)`,
  ]};
  ${({ css }) => css};
`;

const ARTICLE_TypeBox = styled.article`
  ${tw`text-center [& img]:mx-auto`}

  header {
    ${tw`text-[#111] font-extrabold font-NanumSquare`}
    ${tw`text-18 leading-32`}
    ${tw`x_430:(text-18 leading-32)`}
  }
  p {
    ${tw`text-[#333] font-normal font-NotoSansCJKkr`}
    ${tw`text-12 leading-21`}
    ${tw`x_430:(text-11 leading-19)`}
  }
`;

const A_Member = styled.a`
  ${[tw`object-fill rounded-10`, tw`w-[100%] h-[100%]`]};
`;
